import { useGLTF, useTexture } from '@react-three/drei';
import { useControls, button, folder } from 'leva';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three/webgpu';
import {
  texture as textureNode,
  triplanarTexture,
  positionWorld,
  normalWorld,
  cameraViewMatrix,
  uniform,
  vec3,
  vec4,
  abs,
  pow,
  normalize,
} from 'three/tsl';
import { Brush, Evaluator, SUBTRACTION } from 'three-bvh-csg';
import pianoSalvagocciaUrl from './models/piano_salvagoccia.glb';
import baseColorUrl from './materials/mat_1/Vittinox_baseColor.png';
import ormUrl from './materials/mat_1/Vittinox_occlusionRoughnessMetallic.png';
import normalUrl from './materials/mat_1/Vittinox_normal.png';

// Dimensione (in cm) a cui adattare il lato più lungo del modello,
// così riempie l'inquadratura della camera fissa.
const TARGET_SIZE = 120;

// Profilo del foro: rettangolo arrotondato (stessa logica di PianoCucina,
// Esempio 1), centrato nell'origine.
function roundedRectShape(w, h, r) {
  const x = -w / 2;
  const y = -h / 2;
  const s = new THREE.Shape();
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
  s.lineTo(x + w, y + h - r);
  s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
  s.lineTo(x + r, y + h);
  s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(x, y + r);
  s.absarc(x + r, y + r, r, Math.PI, (Math.PI * 3) / 2, false);
  s.closePath();
  return s;
}

// Cutter: profilo arrotondato estruso lungo Y (lo spessore del piano).
// Mappatura come in PianoCucina: shape-X -> larghezza, shape-Y -> profondità,
// estrusione -> altezza (Y).
function roundedRectCutter(w, d, r, height) {
  const geo = new THREE.ExtrudeGeometry(roundedRectShape(w, d, r), {
    depth: height,
    bevelEnabled: false,
    curveSegments: 12,
  });
  geo.translate(0, 0, -height / 2);
  geo.rotateX(-Math.PI / 2);
  geo.computeVertexNormals();
  return geo;
}

// CSG: sottrae i cutter (lungo Y = spessore) dalla geometria in LOCAL-space.
// I fori scalano poi insieme al modello. Coordinate in metri: piano 1.5 x 0.06 x 0.6.
const evaluator = new Evaluator();
evaluator.attributes = ['position', 'normal']; // niente UV: il triplanar le ignora

// Lo stretch su X viene applicato alla geometria del piano PRIMA del taglio,
// così i fori sono ricavati a dimensione reale nello spazio già allargato e
// NON si deformano. Il modello viene poi scalato in modo uniforme.
function buildHoledGeometry(baseGeo, holes, scaleX) {
  const stretched = baseGeo.clone();
  stretched.scale(scaleX, 1, 1);
  if (!holes.length) return stretched;
  let target = new Brush(stretched);
  target.updateMatrixWorld();
  const intermediates = [];
  for (const h of holes) {
    const cutterGeo = roundedRectCutter(h.w, h.d, h.r, 0.3);
    const cutter = new Brush(cutterGeo);
    cutter.position.set(h.x, 0.03, h.z); // centrato nello spessore (Y 0..0.06)
    cutter.updateMatrixWorld();
    target = evaluator.evaluate(target, cutter, SUBTRACTION);
    cutterGeo.dispose();
    intermediates.push(target.geometry);
  }
  // L'ultima geometria è il risultato; le intermedie e il clone si scartano.
  intermediates.pop();
  intermediates.forEach((g) => g.dispose());
  stretched.dispose();
  return target.geometry;
}

export default function PianoSalvagoccia(props) {
  const { scene } = useGLTF(pianoSalvagocciaUrl);

  // Texture del materiale PBR "Vittinox" (packing glTF metal-rough)
  const [map, orm, normal] = useTexture([baseColorUrl, ormUrl, normalUrl]);

  // Prima mesh del modello + sua geometria originale (base per il CSG).
  const mesh = useMemo(() => {
    let found = null;
    scene.traverse((o) => {
      if (!found && o.isMesh) found = o;
    });
    return found;
  }, [scene]);

  const baseGeoRef = useRef(null);
  if (mesh && baseGeoRef.current === null) baseGeoRef.current = mesh.geometry;

  const baseScale = useMemo(() => {
    const box = new THREE.Box3().setFromObject(scene);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    return TARGET_SIZE / maxDim;
  }, [scene]);

  // Solo la Scala X (stretch + re-taglio dell'intero piano) è gated dietro il
  // button. I fori sono economici e restano live.
  const [appliedScalaX, setAppliedScalaX] = useState(1);
  const scalaXRef = useRef(1);

  const { materiale, triScala, nitidezza, intensitaNormal,
    numFori, foroLarghezza, foroProfondita, foroRaggio, scalaX } =
    useControls('Piano salvagoccia', {
      Materiale: folder({
        materiale: { value: true, label: 'Materiale Vittinox' },
        triScala: { value: 0.05, min: 0.005, max: 0.3, step: 0.005, label: 'Scala texture' },
        nitidezza: { value: 4, min: 1, max: 16, step: 0.5, label: 'Nitidezza giunzioni' },
        intensitaNormal: { value: 1, min: 0, max: 3, step: 0.05, label: 'Intensità rilievo' },
      }),
      Fori: folder({
        numFori: { value: 1, min: 0, max: 2, step: 1, label: 'Fori lavandino' },
        foroLarghezza: { value: 0.4, min: 0.1, max: 0.7, step: 0.01, label: 'Larghezza foro (m)' },
        foroProfondita: { value: 0.34, min: 0.1, max: 0.5, step: 0.01, label: 'Profondità foro (m)' },
        foroRaggio: { value: 0.05, min: 0, max: 0.15, step: 0.005, label: 'Raggio angoli (m)' },
      }),
      'Scala X': folder({
        scalaX: { value: 1, min: 0.1, max: 3, step: 0.05, label: 'Scala X' },
        ricalcola: button(() => setAppliedScalaX(scalaXRef.current)),
      }),
    });

  // Tiene il valore corrente di Scala X pronto per il button.
  scalaXRef.current = scalaX;

  // Posizioni dei fori in local-space (metri), live.
  const holes = useMemo(() => {
    const arr = [];
    const w = foroLarghezza;
    const d = foroProfondita;
    const r = foroRaggio;
    if (numFori === 1) arr.push({ x: 0, z: 0, w, d, r });
    if (numFori === 2) {
      arr.push({ x: -0.4, z: 0, w, d, r });
      arr.push({ x: 0.4, z: 0, w, d, r });
    }
    return arr;
  }, [numFori, foroLarghezza, foroProfondita, foroRaggio]);

  // Geometria bucata (CSG). Fori live; Scala X solo al click di "Ricalcola".
  const holedGeo = useMemo(
    () => (mesh ? buildHoledGeometry(baseGeoRef.current, holes, appliedScalaX) : null),
    [mesh, holes, appliedScalaX]
  );

  useEffect(() => {
    if (mesh && holedGeo) mesh.geometry = holedGeo;
  }, [mesh, holedGeo]);

  // Dispose della geometria generata quando cambia (non la base).
  useEffect(() => {
    return () => {
      if (holedGeo && holedGeo !== baseGeoRef.current) holedGeo.dispose();
    };
  }, [holedGeo]);

  // Uniform TSL aggiornabili da leva senza ricompilare lo shader.
  const scaleUniform = useMemo(() => uniform(0.05), []);
  const sharpUniform = useMemo(() => uniform(4), []);
  const normalScaleUniform = useMemo(() => uniform(1), []);

  // MeshStandardNodeMaterial (PBR a nodi) con triplanarTexture: tutto è
  // proiettato in WORLD-space (indipendente dalle UV, scala costante).
  // ORM: AO=R, Rough=G, Metal=B.
  const material = useMemo(() => {
    const m = new THREE.MeshStandardNodeMaterial();

    const colorTri = triplanarTexture(
      textureNode(map), null, null, scaleUniform, positionWorld, normalWorld
    );
    const ormTri = triplanarTexture(
      textureNode(orm), null, null, scaleUniform, positionWorld, normalWorld
    );

    m.colorNode = colorTri;
    m.aoNode = ormTri.r;
    m.roughnessNode = ormTri.g;
    m.metalnessNode = ormTri.b;

    // --- Normal map triplanar (whiteout blend) ---
    const wn = normalize(normalWorld);
    const w = pow(abs(wn), vec3(sharpUniform));
    const blend = w.div(w.x.add(w.y).add(w.z));

    const sampleN = (uv) => {
      const n = textureNode(normal, uv).xyz.mul(2.0).sub(1.0);
      return vec3(n.xy.mul(normalScaleUniform), n.z);
    };
    const nx = sampleN(positionWorld.zy.mul(scaleUniform));
    const ny = sampleN(positionWorld.xz.mul(scaleUniform));
    const nz = sampleN(positionWorld.xy.mul(scaleUniform));

    const tnx = vec3(nx.xy.add(wn.zy), abs(nx.z).mul(wn.x));
    const tny = vec3(ny.xy.add(wn.xz), abs(ny.z).mul(wn.y));
    const tnz = vec3(nz.xy.add(wn.xy), abs(nz.z).mul(wn.z));

    const worldN = normalize(
      tnx.zyx.mul(blend.x)
        .add(tny.xzy.mul(blend.y))
        .add(tnz.xyz.mul(blend.z))
    );

    m.normalNode = normalize(cameraViewMatrix.mul(vec4(worldN, 0.0)).xyz);

    return m;
  }, [map, orm, normal, scaleUniform, sharpUniform, normalScaleUniform]);

  // Color space corretti + wrapping ripetuto (necessario al tiling triplanar).
  useEffect(() => {
    map.colorSpace = THREE.SRGBColorSpace;
    normal.colorSpace = THREE.NoColorSpace;
    orm.colorSpace = THREE.NoColorSpace;
    [map, normal, orm].forEach((t) => {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.needsUpdate = true;
    });
  }, [map, normal, orm]);

  // Aggiorna gli uniform del triplanar quando cambiano i controlli leva.
  useEffect(() => {
    scaleUniform.value = triScala;
    sharpUniform.value = nitidezza;
    normalScaleUniform.value = intensitaNormal;
  }, [triScala, nitidezza, intensitaNormal, scaleUniform, sharpUniform, normalScaleUniform]);

  // Memorizza i materiali originali una sola volta, per poter tornare indietro.
  const originals = useRef(null);
  if (originals.current === null) {
    originals.current = new Map();
    scene.traverse((o) => {
      if (o.isMesh) originals.current.set(o, o.material);
    });
  }

  // Assegna (o ripristina) il materiale alle mesh del modello.
  useEffect(() => {
    scene.traverse((o) => {
      if (o.isMesh) {
        o.material = materiale ? material : originals.current.get(o) ?? o.material;
      }
    });
  }, [scene, material, materiale]);

  useEffect(() => () => material.dispose(), [material]);

  return (
    <primitive
      object={scene}
      scale={baseScale}
      {...props}
    />
  );
}

useGLTF.preload(pianoSalvagocciaUrl);
